<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="5" :sm="24">
              <a-form-item :label="t.code">
                <a-input v-model="queryParam.kocCode" :placeholder="t.codePh" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="5" :sm="24">
              <a-form-item :label="t.type">
                <a-select v-model="queryParam.type" :placeholder="t.all" allow-clear>
                  <a-select-option :value="0">{{ t.gold }}</a-select-option>
                  <a-select-option :value="1">{{ t.vip }}</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="5" :sm="24">
              <a-form-item :label="t.used">
                <a-select v-model="queryParam.useFlag" :placeholder="t.all" allow-clear>
                  <a-select-option :value="0">{{ t.no }}</a-select-option>
                  <a-select-option :value="1">{{ t.yes }}</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="8" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />{{ t.search }}</a-button>
                <a-button style="margin-left: 8px" @click="resetQuery"><a-icon type="redo" />{{ t.reset }}</a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <div class="table-operations">
        <a-space :wrap="true">
          <a-button type="primary" @click="$refs.createForm.handleAdd()" v-hasPermi="['user:koc:add']">
            <a-icon type="plus" />{{ t.add }}
          </a-button>
          <a-button type="danger" :disabled="multiple" @click="handleDelete" v-hasPermi="['user:koc:remove']">
            <a-icon type="delete" />{{ t.del }}
          </a-button>
          <table-setting
            :table-size.sync="tableSize"
            v-model="columns"
            :refresh-loading="loading"
            @refresh="getList" />
        </a-space>
      </div>

      <create-form ref="createForm" @ok="getList" />

      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :pagination="false"
        :bordered="tableBordered"
        :scroll="{ x: 'max-content' }"
      >
        <span slot="createTime" slot-scope="text, record">{{ parseTime(record.createTime) }}</span>
        <span slot="updateTime" slot-scope="text, record">{{ parseTime(record.updateTime) }}</span>
        <span slot="operation" slot-scope="text, record">
          <a-space :size="8">
            <a
              @click="$refs.createForm.handleUpdate(record, undefined)"
              :disabled="record.useFlag === 1"
              v-hasPermi="['user:koc:edit']"
            >
              <a-icon type="edit" />{{ t.edit }}
            </a>
            <a @click="handleDelete(record)" v-hasPermi="['user:koc:remove']" style="color: #ff4d4f;">
              <a-icon type="delete" />{{ t.del }}
            </a>
          </a-space>
        </span>
      </a-table>

      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        :showTotal="total => t.totalPrefix + total + t.totalSuffix"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { pageKoc, delKoc } from '@/api/user/koc'
import CreateForm from './modules/CreateForm'
import { tableMixin } from '@/store/table-mixin'

const T = {
  code: '\u5151\u6362\u7801',
  codePh: '\u8bf7\u8f93\u5165\u5151\u6362\u7801',
  type: '\u7c7b\u578b',
  used: '\u662f\u5426\u4f7f\u7528',
  all: '\u5168\u90e8',
  gold: '\u91d1\u5e01\u5361',
  vip: '\u4f1a\u5458\u5361',
  yes: '\u662f',
  no: '\u5426',
  search: '\u67e5\u8be2',
  reset: '\u91cd\u7f6e',
  add: '\u65b0\u589e',
  edit: '\u4fee\u6539',
  del: '\u5220\u9664',
  totalPrefix: '\u5171 ',
  totalSuffix: ' \u6761',
  value: '\u91d1\u5e01/\u5929\u6570',
  userId: '\u4f7f\u7528\u4ebaID',
  createTime: '\u521b\u5efa\u65f6\u95f4',
  updateTime: '\u4f7f\u7528\u65f6\u95f4',
  action: '\u64cd\u4f5c',
  confirmDel: '\u786e\u8ba4\u5220\u9664\u6240\u9009\u6570\u636e?',
  delOk: '\u5220\u9664\u6210\u529f'
}

export default {
  name: 'Koc',
  components: { CreateForm },
  mixins: [tableMixin],
  data () {
    return {
      t: T,
      list: [],
      selectedRowKeys: [],
      selectedRows: [],
      single: true,
      multiple: true,
      ids: [],
      loading: false,
      total: 0,
      queryParam: {
        kocCode: undefined,
        useFlag: undefined,
        type: undefined,
        pageNum: 1,
        pageSize: 10
      },
      columns: [
        { title: T.code, dataIndex: 'kocCode', ellipsis: true, align: 'center' },
        {
          title: T.type,
          dataIndex: 'type',
          align: 'center',
          customRender: (type) => (type === 0 ? T.gold : type === 1 ? T.vip : '-')
        },
        { title: T.value, dataIndex: 'numberValue', align: 'center' },
        {
          title: T.used,
          dataIndex: 'useFlag',
          align: 'center',
          customRender: (flag) => (flag === 1 ? T.yes : T.no)
        },
        { title: T.userId, dataIndex: 'userId', align: 'center' },
        { title: T.createTime, dataIndex: 'createTime', scopedSlots: { customRender: 'createTime' }, align: 'center' },
        { title: T.updateTime, dataIndex: 'updateTime', scopedSlots: { customRender: 'updateTime' }, align: 'center' },
        { title: T.action, dataIndex: 'operation', width: '18%', scopedSlots: { customRender: 'operation' }, align: 'center' }
      ]
    }
  },
  created () {
    this.getList()
  },
  methods: {
    getList () {
      this.loading = true
      pageKoc(this.queryParam).then(response => {
        this.list = (response.data && response.data.records) || []
        this.total = (response.data && response.data.total) || 0
      }).finally(() => {
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery () {
      this.queryParam = {
        kocCode: undefined,
        useFlag: undefined,
        type: undefined,
        pageNum: 1,
        pageSize: 10
      }
      this.handleQuery()
    },
    onShowSizeChange (current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    changeSize (current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    onSelectChange (selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
      this.ids = this.selectedRows.map(item => item.id)
      this.single = selectedRowKeys.length !== 1
      this.multiple = !selectedRowKeys.length
    },
    handleDelete (row) {
      const ids = row && row.id
        ? [{ id: row.id }]
        : this.selectedRows.map(item => ({ id: item.id }))
      if (!ids.length) return
      const that = this
      this.$confirm({
        title: T.confirmDel,
        content: ids.map(i => i.id).join(','),
        onOk () {
          return delKoc(ids).then(() => {
            that.onSelectChange([], [])
            that.getList()
            that.$message.success(T.delOk)
          })
        }
      })
    }
  }
}
</script>

<style scoped lang="less">
@import '~@/styles/table-responsive.less';
</style>
